import { LoadingButton } from '@mui/lab';
import { Card, IconButton, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { FC, useState } from 'react';
import { DefaultTextAreaField } from 'src/components/mui-overrides/Textarea';
import { Colors } from 'src/components/utils/Theme';
import { Attachment, PostMessagePayload } from 'src/models';
import { array, object, string } from 'yup';
import { UploadIcon } from 'src/components/ui/UploadIcon';
import CloseIcon from '@mui/icons-material/Close';
import { postForumMessage } from 'src/api/stoApi';
import { generalStore } from 'src/stores/GeneralStore';
import { useTranslation } from 'react-i18next';

interface Props {
  reload: () => void;
  username: string;
  assetId: string;
}

export const AddPost: FC<Props> = ({ username, assetId, reload }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const validationSchema = object({
    text: string().required(t('dashboard.assets.forum.addpost.text.required')),
    attachments: array(),
  });

  const formik = useFormik({
    enableReinitialize: true,
    validateOnMount: true,
    initialValues: {
      text: '',
      attachments: [] as Attachment[],
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setIsLoading(true);
      console.log(values);
      const body: PostMessagePayload = {
        assetId: assetId,
        userName: username,
        text: values.text,
        attachments: values.attachments,
      };
      try {
        await postForumMessage(body);
        reload();
      } catch (err) {
        generalStore.setError(t('dashboard.assets.forum.addpost.error'));
      } finally {
        setIsLoading(false);
      }
    },
  });

  const deleteFileClicked = (index: number) => {
    const newAttachments = [...formik.values.attachments];
    newAttachments.splice(index, 1);
    formik.setFieldValue('attachments', newAttachments);
  };

  return (
    <Card
      sx={{
        p: 2,
        mb: 2,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography variant="h6">Add Post</Typography>
      <form onSubmit={formik.handleSubmit}>
        <DefaultTextAreaField
          placeholder={t('dashboard.assets.forum.addpost.text')}
          fieldName={'text'}
          formik={formik}
          label={''}
        ></DefaultTextAreaField>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, paddingBottom: 6 }}>
          {formik.values.attachments.map((attachment: Attachment, index: number) => (
            <div style={{ backgroundColor: Colors.E5GREY, borderRadius: 6, padding: '0 6px' }} key={index}>
              <Typography variant="caption">{attachment.name}</Typography>
              <IconButton size="small" onClick={() => deleteFileClicked(index)}>
                <CloseIcon />
              </IconButton>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <UploadIcon
            newFile={(attachment: Attachment) => {
              formik.setFieldValue('attachments', [...formik.values.attachments, attachment]);
            }}
          />
          <LoadingButton type="submit" variant="contained" loading={isLoading} data-cy="add-forum-post-button">
            {t('dashboard.assets.forum.addpost')}
          </LoadingButton>
        </div>
      </form>
    </Card>
  );
};
